import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Grid, Divider, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import SignaturePad from '../SignaturePad';
import ManagerSelect from '../ManagerSelect';
import { RichText, getFormText, NO_AUTOFILL } from '../PolicyText';

const PayrollAgreementForm = ({ language = 'en', onSubmit, loading, disabled }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    employeeName: '',
    managerName: '',
    hourlyRate: '',
    vacationPay: '',
    totalPay: '',
    yearlySalary: '',
    weeklySalary: '',
    payPeriod: 'hourly'
  });
  const [employeeSignature, setEmployeeSignature] = useState(null);
  const [managerSignature, setManagerSignature] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
      setFormData(prev => ({ ...prev, employeeName: fullName }));
    }
  }, [user]);

  const labels = language === 'es' ? {
    title: 'Acuerdo de Nómina',
    employeeName: 'Nombre del Empleado',
    managerName: 'Nombre del Gerente',
    hourlyRate: 'Tarifa por Hora',
    payPeriod: 'Período de Pago',
    hourly: 'Por Hora',
    yearly: 'Salario',
    employeeSignature: 'Firma del Empleado',
    managerSignature: 'Firma del Gerente',
    save: 'Guardar',
    required: 'Obligatorio',
    signatureRequired: 'Se requiere firma'
  } : {
    title: 'Payroll Agreement',
    employeeName: 'Employee Name',
    managerName: 'Manager Name',
    hourlyRate: 'Hourly Rate',
    payPeriod: 'Pay Period',
    hourly: 'Hourly',
    yearly: 'Salary',
    employeeSignature: 'Employee Signature',
    managerSignature: 'Manager Signature',
    save: 'Save Form',
    required: 'Required',
    signatureRequired: 'Signature required'
  };

  // Legacy PL Hub agreement wording (translation.json employeeForms.text.payroll).
  const wording = getFormText(language, 'text.payroll');
  const isSalary = formData.payPeriod === 'salary' || formData.payPeriod === 'yearly';

  // Legacy: weekly salary = yearly / 52, shown read-only.
  const handleYearlyChange = (value) => {
    const yearly = parseFloat(value);
    setFormData((prev) => ({
      ...prev,
      yearlySalary: value,
      weeklySalary: Number.isFinite(yearly) ? (yearly / 52).toFixed(2) : ''
    }));
  };

  // Legacy cleared the other pay type's amounts when switching tabs.
  const handlePayPeriodChange = (payPeriod) => {
    setFormData((prev) => ({
      ...prev,
      payPeriod,
      hourlyRate: '',
      vacationPay: '',
      totalPay: '',
      yearlySalary: '',
      weeklySalary: ''
    }));
    setErrors({});
  };

  const amountField = (name, label, extra = {}) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <RichText text={label} values={{ name: formData.employeeName }} />
      </Typography>
      <TextField
        name={name}
        type="number"
        size="small"
        value={formData[name]}
        onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
        disabled={disabled}
        error={!!errors[name]}
        helperText={errors[name]}
        inputProps={{ min: 0, step: '0.01', 'aria-label': label.replace(/\*\*|\[\[name\]\]/g, '').trim() }}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
          endAdornment: extra.perHour ? <InputAdornment position="end">{wording.perHour}</InputAdornment> : undefined
        }}
        sx={{ maxWidth: 280 }}
        {...extra.props}
      />
    </Box>
  );

  const validate = () => {
    const newErrors = {};
    if (!formData.employeeName.trim()) newErrors.employeeName = labels.required;
    const amounts = isSalary ? ['yearlySalary'] : ['hourlyRate', 'vacationPay', 'totalPay'];
    amounts.forEach((name) => {
      if (String(formData[name]).trim() === '') newErrors[name] = labels.required;
    });
    if (!formData.managerName.trim()) newErrors.managerName = labels.required;
    if (!employeeSignature) newErrors.employeeSignature = labels.signatureRequired;
    if (!managerSignature) newErrors.managerSignature = labels.signatureRequired;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ ...formData, employeeSignature, managerSignature }, employeeSignature);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} autoComplete="off" data-lpignore="true" data-form-type="other">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth label={labels.employeeName} name="employeeName" value={formData.employeeName} 
            onChange={(e) => setFormData({...formData, employeeName: e.target.value})} required disabled={disabled} inputProps={NO_AUTOFILL} 
            error={!!errors.employeeName} helperText={errors.employeeName} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>{labels.payPeriod}</InputLabel>
            {/* TKT-199: keep accepting the legacy 'yearly' value for any
                existing saved forms but the canonical option is 'salary'
                so the dropdown text matches Naomi's expected wording. */}
            <Select name="payPeriod" value={formData.payPeriod === 'yearly' ? 'salary' : formData.payPeriod} label={labels.payPeriod}
              onChange={(e) => handlePayPeriodChange(e.target.value)} disabled={disabled}>
              <MenuItem value="hourly">{labels.hourly}</MenuItem>
              <MenuItem value="salary">{labels.yearly}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ManagerSelect
            label={labels.managerName}
            value={formData.managerName}
            onChange={(managerName) => setFormData({ ...formData, managerName })}
            required
            disabled={disabled}
            error={!!errors.managerName}
            helperText={errors.managerName}
            language={language}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        {isSalary ? (
          <>
            {amountField('yearlySalary', wording.yearlySalary, { props: { onChange: (e) => handleYearlyChange(e.target.value) } })}
            {amountField('weeklySalary', wording.weeklySalary, { props: { InputProps: { readOnly: true, startAdornment: <InputAdornment position="start">$</InputAdornment> } } })}
          </>
        ) : (
          <>
            {amountField('hourlyRate', wording.hourlyRate, { perHour: true })}
            {amountField('vacationPay', wording.vacationPay, { perHour: true })}
            {amountField('totalPay', wording.totalPay, { perHour: true })}
          </>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />
      
      <SignaturePad onSignature={setEmployeeSignature} label={labels.employeeSignature} language={language} />
      {errors.employeeSignature && <Typography color="error" variant="caption">{errors.employeeSignature}</Typography>}
      
      <Divider sx={{ my: 3 }} />
      
      <SignaturePad onSignature={setManagerSignature} label={labels.managerSignature} language={language} />
      {errors.managerSignature && <Typography color="error" variant="caption">{errors.managerSignature}</Typography>}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" variant="contained" size="large" startIcon={<SaveIcon />} disabled={disabled || loading}>
          {labels.save}
        </Button>
      </Box>
    </Box>
  );
};

export default PayrollAgreementForm;

